import { useCallback, useEffect, useRef, useState } from "react";

// Fetch-once data hook for the BSS dashboard. Loads the resource on
// mount and then re-fetches ONLY when the caller invokes `refresh()` (wired to
// a manual Refresh button). It deliberately does NOT poll on an interval: the
// dashboard previously re-requested every few seconds on every open tab, which
// was a lot of wasted load — users now pull fresh data on demand.
//
// - `fetcher` is an async function returning the resource.
// - `enabled` (default true): when false, no fetch is issued and `loading` is
//   false (callers gate on a prerequisite, e.g. a route param).
// - `deps` (default []): a param-driven dependency list. When any value in it
//   changes (page, search query, filter tab, …) the hook re-fetches. This is
//   NOT polling — there is still no interval; a re-fetch only happens when the
//   caller's params actually change. The fetcher closure reads the latest
//   params, so callers should recreate `fetcher` (e.g. via useCallback) with
//   the current params alongside bumping `deps`.
// - `stale` is true when the most recent refresh FAILED but we are still
//   showing previously-loaded data — use it for a non-blocking stale
//   indicator. An initial-load failure (no data yet) surfaces as `error` with
//   `data === null` so callers can render a blocking ErrorState instead.
//
// NOTE: the name is retained for import stability across the dashboard; this
// hook no longer polls. Legacy `interval` / `shouldContinue` options are
// accepted-and-ignored so existing call sites do not break.
export default function useBssPoll(fetcher, { enabled = true, deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [stale, setStale] = useState(false);

  const fetcherRef = useRef(fetcher);
  const mountedRef = useRef(true);
  const seqRef = useRef(0);
  const dataRef = useRef(null);

  fetcherRef.current = fetcher;

  // Single fetch with LAST-WRITE-WINS semantics. Each call takes a sequence
  // number; only the latest call's response is applied. This is essential for
  // `deps`-driven refetch + pagination: a fast page/filter/search change while
  // a request is in flight must NOT be dropped (an in-flight *guard* would
  // strand the new params on stale data) and an out-of-order earlier response
  // must NOT clobber a newer one. Also guards against setState-after-unmount.
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    const fresh = () => mountedRef.current && seq === seqRef.current;
    setLoading(true);
    try {
      const result = await fetcherRef.current();
      if (!fresh()) return; // superseded by a newer load (or unmounted)
      dataRef.current = result;
      setData(result);
      setError(null);
      setStale(false);
    } catch (err) {
      if (!fresh()) return;
      setError(err);
      // Keep prior data; only flag stale if we already had data to show.
      if (dataRef.current != null) setStale(true);
    } finally {
      if (fresh()) setLoading(false);
    }
  }, []);

  // Manual refresh — what the Refresh button calls.
  const refresh = useCallback(() => load(), [load]);

  // Serialize `deps` so the effect re-runs on a value change (not identity).
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    load();
    return () => {
      mountedRef.current = false;
    };
    // `depsKey` is the param-driven trigger: a page / query / filter change
    // re-runs the fetch (the serialized form of the caller's `deps`).
  }, [enabled, load, depsKey]);

  return { data, error, loading, stale, refresh };
}
